'use client';

import clsx from 'clsx';
import type { ForwardedRef, ReactNode } from 'react';
import { Link as RACLink, type LinkProps as RACLinkProps } from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { withStyleProps } from '../../with-style-props.js';
import type { NavigationProps, WithRequired } from '../types.js';
import type { BaseTypographyProps, TextStyleOptions } from './types.js';
import { textStyle } from './utils.js';

// Used to prevent word wrap between icons and text
const WORD_JOINER = '\u2060';

export interface LinkProps
  extends
    Omit<BaseTypographyProps, 'id'>,
    WithRequired<NavigationProps, 'href'>,
    Pick<TextStyleOptions, 'variant' | 'fontStyle' | 'alignment'>,
    Pick<RACLinkProps, 'isDisabled' | 'onHoverStart' | 'onHoverEnd'> {
  /** An icon displayed before the link text. */
  iconStart?: ReactNode;
  /** An icon displayed after the link text. */
  iconEnd?: ReactNode;
}

function Link(
  { children, iconStart, iconEnd, UNSAFE_className, UNSAFE_style, variant, fontStyle, alignment, ...props }: LinkProps,
  ref: ForwardedRef<HTMLAnchorElement>,
) {
  return (
    <RACLink
      {...props}
      ref={ref}
      className={clsx('cim-link', textStyle({ tone: 'accent', variant, fontStyle, alignment }), UNSAFE_className)}
      style={UNSAFE_style}
    >
      {iconStart && (
        <>
          <span className="cim-link-icon">{iconStart}</span>
          {WORD_JOINER}
        </>
      )}
      <span className="cim-link-text">{children}</span>
      {iconEnd && (
        <>
          {WORD_JOINER}
          <span className="cim-link-icon">{iconEnd}</span>
        </>
      )}
    </RACLink>
  );
}

/**
 * Displays a textual link that allows users to navigate to another page or resource.
 *
 * See [link usage guidelines](https://ui.cimpress.io/components/link/).
 */
const _Link = withStyleProps(forwardRef(Link), 'Link');

export { _Link as Link };
